import { t } from '../i18n';
// admin-client/src/pages/LoungeManagement.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getStoredAdminUser } from '../auth/adminSession';
import { fetchMyLoungePermissions } from '../api/adminPermissionApi';
import LoungeUploadBanner from '../components/lounge/LoungeUploadBanner';
import LoungeItemCard from '../components/lounge/LoungeItemCard';
import LoungeItemModal from '../components/lounge/LoungeItemModal';
import LoungeWidgetOrdersPanel from '../components/lounge/LoungeWidgetOrdersPanel';
import { createLoungePost, deleteLoungePost, fetchAdminLoungePosts, updateLoungePost, DEFAULT_CATEGORIES, normalizeLoungeCategoryOptions, } from '../api/loungeApi';
function makeDraft(uploadFee = 60, periodDays = 30) {
    return {
        category: 'en',
        title: '',
        thumbnailUrl: '',
        contentJson: [
            {
                id: Math.random().toString(36).slice(2, 10),
                type: 'text',
                text: '',
                style: {
                    textType: 'paragraph',
                    fontSize: 16,
                    fontWeight: 'normal',
                    color: '#ffffff',
                },
            },
        ],
        status: 'draft',
        uploadFee,
        periodDays,
        isPaid: false,
        paymentOptions: [],
    };
}
export default function LoungeManagement() {
    const [items, setItems] = useState([]);
    const [permissionLoading, setPermissionLoading] = useState(true);
    const [permission, setPermission] = useState({});
    const ownUserCode = String(getStoredAdminUser()?.user_code || '');
    const isOwner = (item) => String(item?.agentUserCode || '') === ownUserCode;
    const canOperate = (item, key) => permission[key] === true && (isOwner(item) || permission.manage_all === true);
    useEffect(() => {
      const abort = new AbortController();
      fetchMyLoungePermissions(abort.signal).then((data) => setPermission(data.permissions || {})).catch(() => setPermission({})).finally(() => { if (!abort.signal.aborted) setPermissionLoading(false); });
      return () => abort.abort();
    }, []);
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [uploadFee, setUploadFee] = useState(60);
    const [periodDays, setPeriodDays] = useState(30);
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [editingItem, setEditingItem] = useState(null);
    const filteredItems = useMemo(() => items, [items]);
    const loadItems = useCallback(async ({ signal } = {}) => {
        setLoading(true);
        setError('');
        try {
            const result = await fetchAdminLoungePosts({
                status: statusFilter,
                category: categoryFilter,
                page: 1,
                limit: 100,
                signal,
            });
            setItems(result.items);
            setUploadFee(result.uploadFee || 60);
            setPeriodDays(result.periodDays || 30);
            setCategories(normalizeLoungeCategoryOptions(result.categories));
        }
        catch (err) {
            console.error('[LoungeManagement] fetch failed:', err);
            setError(err?.message || t("\uB77C\uC6B4\uC9C0 \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4."));
            setItems([]);
        }
        finally {
            setLoading(false);
        }
    }, [statusFilter, categoryFilter]);
    useEffect(() => {
        const controller = new AbortController();
        loadItems({ signal: controller.signal });
        return () => controller.abort();
    }, [loadItems]);
    const openCreate = () => {
        if (submitting || !permission.create)
            return;
        setModalMode('create');
        setEditingItem(makeDraft(uploadFee, periodDays));
        setModalOpen(true);
    };
    const openEdit = (item) => {
        if (submitting || !canOperate(item, 'edit') || (item.status === 'published' && !permission.publish))
            return;
        setModalMode('edit');
        setEditingItem(item);
        setModalOpen(true);
    };
    const closeModal = () => {
        if (submitting)
            return;
        setModalOpen(false);
    };
    const savePayload = async (payload) => {
        if (payload.status === 'published' && !permission.publish) { alert('라운지 게시 권한이 없습니다.'); return; }
        setSubmitting(true);
        try {
            if (modalMode === 'create') {
                await createLoungePost(payload);
            }
            else {
                await updateLoungePost(editingItem.postCode, payload);
            }
            setModalOpen(false);
            await loadItems();
        }
        catch (err) {
            console.error('[LoungeManagement] save failed:', err);
            alert(t('라운지 저장에 실패했습니다.\n{message}', { message: err.message || err }));
        }
        finally {
            setSubmitting(false);
        }
    };
    const publishItem = async (item) => {
        if (!canOperate(item, 'publish')) return;
        const endAt = item?.publishEndAt || item?.expiredAt || null;
        const endMs = endAt ? new Date(endAt).getTime() : 0;
        const hasActivePeriod = Number.isFinite(endMs) && endMs > Date.now();
        const hasPayment = Boolean(item?.isPaid || item?.paidAt);
        const hasActivePayment = hasPayment && hasActivePeriod;
        const needPayment = !hasActivePayment;
        if (needPayment) {
            const ok = window.confirm(t('업로드 비용 {fee} USDT가 UserDB.asset에서 차감되고, 결제일 기준 {days}일 동안 게시됩니다. 게시할까요?', { fee: Number(item.uploadFee || uploadFee || 60).toLocaleString(), days: Number(item.periodDays || periodDays || 30) }));
            if (!ok)
                return;
        }
        setSubmitting(true);
        try {
            await updateLoungePost(item.postCode, { status: 'published' });
            await loadItems();
        }
        catch (err) {
            console.error('[LoungeManagement] publish failed:', err);
            alert(t('라운지 게시에 실패했습니다.\n{message}', { message: err.message || err }));
        }
        finally {
            setSubmitting(false);
        }
    };
    const pauseItem = async (item) => {
        if (!canOperate(item, 'publish')) return;
        const ok = window.confirm(t('이 라운지 글을 중지할까요?\n\n- {title}', { title: item.title }));
        if (!ok)
            return;
        setSubmitting(true);
        try {
            await updateLoungePost(item.postCode, { status: 'paused' });
            await loadItems();
        }
        catch (err) {
            console.error('[LoungeManagement] pause failed:', err);
            alert(t('라운지 중지에 실패했습니다.\n{message}', { message: err.message || err }));
        }
        finally {
            setSubmitting(false);
        }
    };
    const removeItem = async (item) => {
        if (!canOperate(item, 'delete')) return;
        const ok = window.confirm(t('이 라운지 글을 삭제 처리할까요?\n\n- {title}', { title: item.title }));
        if (!ok)
            return;
        setSubmitting(true);
        try {
            await deleteLoungePost(item.postCode);
            await loadItems();
        }
        catch (err) {
            console.error('[LoungeManagement] delete failed:', err);
            alert(t('라운지 삭제에 실패했습니다.\n{message}', { message: err.message || err }));
        }
        finally {
            setSubmitting(false);
        }
    };
    return (<div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-start sm:items-center gap-2">
          <span className="text-lg">🪑</span>
          <div>
            <h1 className="text-lg font-semibold">{t("\uB77C\uC6B4\uC9C0 \uC544\uC774\uD15C")}</h1>
            <p className="text-xs text-gray-500 mt-1">{t("DB/API \uAE30\uBC18 \uC5D0\uC774\uC804\uD2B8 \uC720\uB8CC \uC5C5\uB85C\uB4DC \uAD00\uB9AC")}</p>
          </div>
        </div>

        <button type="button" onClick={() => loadItems()} disabled={loading || submitting} className="w-full sm:w-auto px-3 py-2 rounded-md border border-gray-200 text-sm hover:bg-gray-50 disabled:opacity-50">{t("\uC0C8\uB85C\uACE0\uCE68")}</button>
      </div>

      {!permissionLoading && !permission.create && <p className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">라운지 글 작성은 승인된 관리자 계정만 가능합니다. LV0 관리자에게 권한 승인을 요청하세요.</p>}
      <LoungeUploadBanner onClick={openCreate} disabled={permissionLoading || !permission.create} uploadFee={uploadFee} periodDays={periodDays}/>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" disabled={loading || submitting}>
          <option value="all">{t("\uC804\uCCB4 \uC0C1\uD0DC")}</option>
          <option value="draft">{t("\uCD08\uC548")}</option>
          <option value="published">{t("\uAC8C\uC2DC \uC911")}</option>
          <option value="paused">{t("\uC911\uC9C0\uB428")}</option>
          <option value="deleted">{t("\uC0AD\uC81C\uB428")}</option>
        </select>

        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" disabled={loading || submitting}>
          <option value="all">ALL · 전체 언어</option>
          {categories.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
        </select>

        {submitting ? <span className="text-xs text-blue-600">{t("\uCC98\uB9AC \uC911...")}</span> : null}
      </div>

      <div className="mt-8">
        <div className="text-sm font-semibold text-gray-700 mb-3">{t("\uB77C\uC6B4\uC9C0 \uAE00 \uBAA9\uB85D")}</div>

        {error ? (<div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
            {error}
          </div>) : loading ? (<div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">{t("\uB77C\uC6B4\uC9C0 \uBAA9\uB85D \uBD88\uB7EC\uC624\uB294 \uC911...")}</div>) : filteredItems.length ? (<div className="flex flex-col gap-4">
            {filteredItems.map((item) => (<LoungeItemCard key={item.postCode || item.id} item={item} onClick={() => openEdit(item)} onPublish={publishItem} onPause={pauseItem} onDelete={removeItem} canEdit={canOperate(item, 'edit') && (item.status !== 'published' || permission.publish)} canPublish={canOperate(item, 'publish')} canDelete={canOperate(item, 'delete')}/>))}
          </div>) : (<div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">{t("\uB4F1\uB85D\uB41C \uB77C\uC6B4\uC9C0 \uAE00\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.")}</div>)}
      </div>

      {permission.manage_all===true && <LoungeWidgetOrdersPanel />}
      <LoungeItemModal open={modalOpen} mode={modalMode} initialValue={editingItem || makeDraft(uploadFee, periodDays)} uploadFee={uploadFee} categories={categories} submitting={submitting} canPublish={permission.publish === true} onClose={closeModal} onSave={(payload) => savePayload(payload)} onUpload={(payload) => savePayload(payload)}/>
    </div>);
}
